import { applyParentChildToggle, filterOptionsByQuery } from '../../../common/filters/utils';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  ScrollArea,
  TextInput,
  Text,
  Highlight,
  Flex,
  Box,
  Avatar,
  Stack,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { IconChevronDown, IconChevronRight, IconFolder, IconListSearch } from "@tabler/icons";

import { capitalizeFirstLetter } from "../../../../utils/capitalizeFirstLetter";
import { useFiltersContextHelper } from '../useFiltersContextHelper';
import OperationSelect from "../OperationSelect";
import AddFilterBtn from "../AddFilterBtn";
import { Filter } from "../FiltersMenu";
import FilterFooter from "../FilterFooter";
import { findFiltersByPath } from "../../../../context/helpers";
import { buildSelectedFilterMapFromOptions } from "../../../explore/utils/buildSessionFilters";
import { DomainHeaderIcon, ProductAreaHeaderIcon } from "../../../../icons/x-symbol-svgrepo-com";
import {
  RELEASE_FILTER_KEY,
  RELEASE_GROUP_OPTION_TYPE,
} from "../../../../constants/releases";
import {
  groupReleaseOptions,
  isReleaseGroupOptionValue,
} from "../../../../utils/releaseOptions";
import { getInitials } from "../../../../utils/getInitials";
import { SourceIcon } from "../../../../utils/SourceIcon";
import _ from "lodash";

interface CheckboxFilterProps {
  initialfilters: Filter;
  MenuType: "idea" | "evidence";
  resetFilters: (
    MenuType: "idea" | "evidence",
    selectedParentFilter?: string | null
  ) => void;
  ideasFiltersConfig: any;
  // Optional external control for menu open state (e.g., sticky filters)
  setOpened?: (open: boolean) => void;
  setValue?: (value: string[] | null) => void;
  hideAddButton?: boolean;
  /** Footer wording for the clear action — see `FilterFooter`. */
  clearLabel?: string;
}

/**
 * Hoisted out of the render so emotion sees the same object every time. A
 * fresh `sx` literal per row is re-serialised on every render, and the release
 * tree renders hundreds of rows.
 */
const CHECKBOX_SX = {
  cursor: "pointer",
  input: {
    ":checked": {
      backgroundColor: "#5B60E3",
      borderColor: "#5B60E3",
    },
  },
};

const OWNER_CHECKBOX_STYLES = { body: { alignItems: "center" } };

const ICON_COLOR = "#adb5bd";

interface OptionRowProps {
  option: any;
  pathKey: string;
  hasChildren: boolean;
  isOpen: boolean;
  isOwnerFilter: boolean;
  isSourceFilter: boolean;
  isProductAreaFilter: boolean;
  isReleaseFilter: boolean;
  isReleaseGroupRow: boolean;
  isUntickableReleaseGroup: boolean;
  isCurrentUser: boolean;
  query: string;
  onToggle: (pathKey: string) => void;
}

/**
 * One row of the tree — the chevron, the checkbox and the label — behind a
 * `React.memo` boundary.
 *
 * Expanding or collapsing a group changes `openPaths`, which re-renders the
 * whole filter. Without this boundary every *other* row re-rendered with it,
 * and a row is a Mantine `Checkbox` plus a `Highlight`: on a release tree a
 * few hundred rows deep that turned one chevron click into hundreds of
 * milliseconds of redrawing rows that had not changed. Every prop here is a
 * primitive or a stable reference — the option objects come from
 * `optionsToRender`'s `useMemo`, `onToggle` from a `useCallback` — so only the
 * row whose `isOpen` actually flipped does any work.
 */
const OptionRow: React.FC<OptionRowProps> = React.memo(({
  option,
  pathKey,
  hasChildren,
  isOpen,
  isOwnerFilter,
  isSourceFilter,
  isProductAreaFilter,
  isReleaseFilter,
  isReleaseGroupRow,
  isUntickableReleaseGroup,
  isCurrentUser,
  query,
  onToggle,
}) => {
  const optionValue = String(option.value || option.label || '').toLowerCase();

  const optionLabel = (
    isOwnerFilter ? (
      <Box>
        <Flex align="center" gap={6}>
          {optionValue !== 'unassigned' && (
            <Avatar src={option.picture} radius="xl" size={28}>
              {option.name ? getInitials(option.name) : ''}
            </Avatar>
          )}
          <Stack spacing={0}>
            <Text size="sm">
              <Highlight highlight={query}>
                {isCurrentUser
                  ? `${capitalizeFirstLetter(String(option.label || option.name || ''))} (me)`
                  : capitalizeFirstLetter(String(option.label || option.name || ''))}
              </Highlight>
            </Text>
            {option.email && (
              <Text size="xs" c="dimmed">
                <Highlight highlight={query}>
                  {String(option.email || '')}
                </Highlight>
              </Text>
            )}
          </Stack>
        </Flex>
      </Box>
    ) : isSourceFilter ? (
      <Flex align="center" gap={6}>
        <Avatar radius="xl" size={28}>
          <SourceIcon sourceName={String(option.value || option.label || '')} width={16} height={16} />
        </Avatar>
        <Text size="sm">{capitalizeFirstLetter(String(option.label || option.name || ''))}</Text>
      </Flex>
    ) : (
      <Text style={{ display: "flex", alignItems: "center"}}>
        {isProductAreaFilter && <Box style={{ display: "flex", alignItems: "center", marginRight:"7px"}}>
          {(option.type == "domain") ? (
            <DomainHeaderIcon fill={ICON_COLOR} color={ICON_COLOR} />
          ) : (
            <ProductAreaHeaderIcon fill={ICON_COLOR} color={ICON_COLOR} />
          )}
        </Box>}
        {/* A release row carries no icon: the dot it used to show was a colour swatch, and
            releases have no colour any more. The group row keeps its folder. */}
        {isReleaseFilter && isReleaseGroupRow && (
          <Box style={{ display: "flex", alignItems: "center", marginRight: "7px" }}>
            <IconFolder size={14} color={ICON_COLOR} />
          </Box>
        )}
        <Highlight highlight={capitalizeFirstLetter(query)}>
          {capitalizeFirstLetter(String(option.label || option.name || ''))}
        </Highlight>
      </Text>
    )
  );

  /**
   * Releases widen the hit area to the whole gutter: a group row may have no
   * checkbox of its own, so the chevron is the only way in. Product-area keeps
   * its original icon-only target.
   *
   * Whichever element owns the toggle, only *one* of them may: a click on the
   * chevron bubbles to the gutter, so handlers on both fired twice in the same
   * batch and the second toggle undid the first. Clicking the chevron - the
   * obvious target - then did nothing at all, and only the few pixels of
   * padding beside it worked.
   */
  const gutterToggles = isReleaseFilter && hasChildren;
  const chevronProps = gutterToggles
    ? { size: 14, color: ICON_COLOR }
    : { size: 14, color: ICON_COLOR, onClick: () => onToggle(pathKey) };

  return (
    <Flex>
      {(isProductAreaFilter || isReleaseFilter) && <Box
        onClick={gutterToggles ? () => onToggle(pathKey) : undefined}
        data-testid={isReleaseGroupRow ? "release-group-toggle" : undefined}
        data-group-name={isReleaseGroupRow ? String(option.label || '') : undefined}
        style={{ paddingLeft: hasChildren ? "10px" : "24px", display: "flex", alignItems: "center", cursor: gutterToggles ? "pointer" : undefined }}>
        {hasChildren && (isOpen
          ? <IconChevronDown {...chevronProps} />
          : <IconChevronRight {...chevronProps} />)}
      </Box>}

      {isUntickableReleaseGroup ? (
        <Box
          my={5}
          py={3}
          pl="sm"
          pr="sm"
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={() => onToggle(pathKey)}
        >
          {optionLabel}
        </Box>
      ) : (
        <Checkbox
          my={5}
          sx={CHECKBOX_SX}
          styles={isOwnerFilter ? OWNER_CHECKBOX_STYLES : undefined}
          pr="sm"
          pl={"sm"}
          py={3}
          value={optionValue}
          label={optionLabel}
        />
      )}
    </Flex>
  );
});

OptionRow.displayName = "CheckboxFilterOptionRow";

const CheckboxFilter: React.FC<CheckboxFilterProps> = ({
  MenuType,
  resetFilters,
  ideasFiltersConfig,
  setOpened,
  setValue,
  hideAddButton,
  clearLabel,
}) => {
  const filtersContext = useFiltersContextHelper();
  const {
    activeFilters,
    setActiveFilters,
    selectedParentFilter,
    checkedComponents,
    setCheckedComponents,
    childFiltersValue,
    childQuery,
    setChildQuery,
    operator,
    setOperator,
    setOpenedMenuType,
    setSelectedParentFilter,
  } = filtersContext;

  const [openPaths, setOpenPaths] = useState<Set<string>>(new Set());
  const [localSearchQuery, setLocalSearchQuery] = useState<string>('');
  const [debouncedQuery] = useDebouncedValue(localSearchQuery, 300);
  const auth0 = useAuth0();
  const currentUserEmail = auth0.user?.email?.toLowerCase();

  const _selectedParentFilter: string[] =
      selectedParentFilter?.split(".") || [""];

  // Function to recursively get the selected filter based on the path
  const getSelectedFilter = (config: any, path: string[]): any => {
    let current = config[MenuType];

    for (const key of path) {
      if (current && current[key]) {
        current = current[key];
      } else if (current && current.childFilters) {
        // Try to find the filter in childFilters by key or by name (case-insensitive)
        const keys = Object.keys(current.childFilters);
        const matchingKey = keys.find(
          (k) => k.toLowerCase().replace(/\s+/g, "-") === key.toLowerCase()
        );

        if (matchingKey) {
          current = current.childFilters[matchingKey];
        } else {
          // Try to find by 'name' property
          const matchingFilter = Object.values(current.childFilters).find(
            (filter: any) => filter.name.toLowerCase() === key.toLowerCase()
          );
          if (matchingFilter) {
            current = matchingFilter;
          } else {
            return undefined;
          }
        }
      } else {
        return undefined;
      }
    }
    return current;
  };

  const selectedFilter = getSelectedFilter(
    ideasFiltersConfig,
    _selectedParentFilter
  );
  // console.log({selectedFilter});

  const selectedHasOperations = selectedFilter?.operations?.length > 0 || false;
  const selectedOperators = selectedFilter?.operations;
  const selectedOptions = selectedFilter?.options;

  useEffect(() => {
    const currentFilters: any = activeFilters.find(
      (aF: any) => aF.parent === selectedParentFilter
    );

    if (currentFilters?.selected && selectedParentFilter && currentFilters) {
      setCheckedComponents((prev: any) => ({
        ...prev,
        [selectedParentFilter]: currentFilters.selected.map((o: string) =>
          o.toLowerCase()
        ),
      }));
    }
  }, []);

  // Stable, so the memoised rows below are not invalidated on every render.
  const toggleOpen = useCallback((path: string) => {
    setOpenPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  }, []);

  const processedOptions = useMemo(() => {
    if (!selectedOptions) return [];

    // Group product-areas by their domain field directly from selectedOptions
    if (selectedParentFilter === "product-area" || selectedParentFilter === "evidence-product-area") {
      // Flatten product-areas
      const productAreas = Array.isArray(selectedOptions)
        ? selectedOptions
        : Object.values(selectedOptions);

      // Group by domain
      const domainMap: { [domain: string]: any[] } = {};
      productAreas.forEach((pa: any) => {
        const domain = pa.domain || "Unassigned";
        if (!domainMap[domain]) domainMap[domain] = [];
        domainMap[domain].push(pa);
      });

      return Object.entries(domainMap).map(([domain, childComponents]) => ({
        label: domain,
        value: domain,
        type: "domain",
        childComponents,
      }));
    }

    // Releases nest the same way, one level deeper in meaning: the synthetic
    // parent is a release *group*, and unlike a domain it is never itself a
    // submittable value — `toReleaseIds` strips it back out on the way to the
    // server. Group order and release order are already fixed by the flat list
    // (`buildReleaseOptions` walks groups by index), so first-seen wins here.
    if (selectedParentFilter === RELEASE_FILTER_KEY) {
      const releaseOptions = Array.isArray(selectedOptions)
        ? selectedOptions
        : Object.values(selectedOptions);

      const grouped = groupReleaseOptions(releaseOptions);

      if (debouncedQuery) {
        return filterOptionsByQuery(grouped, debouncedQuery);
      }

      return grouped;
    }

    if (Array.isArray(selectedOptions)) {
      return selectedOptions.map((option: any) => ({
        label: option?.label?.label || option.label,
        value: option?.value?.label || option.value,
        childComponents: option.childComponents || [],
        email: option.email,
        name: option.name,
        picture: option.picture,
      }));
    }

    if (typeof selectedOptions === "object") {
      return Object.entries(selectedOptions).map(
        ([key, value]: [string, any]) => ({
          label: value.name || value.label || key,
          value: value.value || key,
          childComponents: value.childComponents || [],
          email: value.email,
          name: value.name,
          picture: value.picture,
        })
      );
    }

    return [];
  }, [selectedOptions, selectedParentFilter]);

  const optionsToRender = useMemo(() => {
    let optionsList: any[] = processedOptions;

    if (debouncedQuery) {
      const isOwnerFilter = selectedParentFilter === "owner" || selectedParentFilter === "evidence-owner";
      optionsList = filterOptionsByQuery(optionsList, debouncedQuery, isOwnerFilter);
    }

    // Ensure current user appears first in Owner filters
    const isOwnerFilter = selectedParentFilter === "owner" || selectedParentFilter === "evidence-owner";
    if (isOwnerFilter && currentUserEmail && Array.isArray(optionsList) && optionsList.length > 0) {
      const idx = optionsList.findIndex((o: any) => (o.email || "").toLowerCase() === currentUserEmail);
      if (idx > 0) {
        optionsList = [...optionsList];
        const [me] = optionsList.splice(idx, 1);
        optionsList.unshift(me);
      }
    }

    return optionsList;
  }, [processedOptions, debouncedQuery, selectedParentFilter, currentUserEmail]);

  // Helper to collect all ancestor parent paths for visible (matched) items
  const collectParentPaths = (options: any[], parentPath: string[] = [], acc: Set<string> = new Set(), lowerQuery = ""): Set<string> => {
    if (!options || !Array.isArray(options)) return acc;
    for (const option of options) {
      if (!option) continue;
      const optionValue = String(option.value || option.label || '').toLowerCase();
      const labelStr = String(option.label || option.name || '').toLowerCase();
      const currentPath = [
        ...parentPath,
        optionValue,
      ];
      const pathKey = currentPath.join(">");
      let matched = false;
      if (lowerQuery && labelStr.includes(lowerQuery)) {
        // If this node matches, add all ancestor paths (not including itself)
        for (let i = 1; i < currentPath.length; ++i) {
          acc.add(currentPath.slice(0, i).join(">"));
        }
        matched = true;
      }
      if (option.childComponents && option.childComponents.length > 0) {
        const before = acc.size;
        collectParentPaths(option.childComponents, currentPath, acc, lowerQuery);
        if (!matched && acc.size > before) {
          acc.add(pathKey);
        }
      }
    }
    return acc;
  };

  const autoOpenPaths = useMemo(() => {
    if (!debouncedQuery) return new Set();
    return collectParentPaths(optionsToRender, [], new Set(), debouncedQuery.toLowerCase());
  }, [optionsToRender, debouncedQuery]);

  // Automatically open paths for checked options
  useEffect(() => {
    if (!selectedParentFilter || !optionsToRender || optionsToRender.length === 0) return;
    
    const checkedValues = checkedComponents[selectedParentFilter] || [];
    if (checkedValues.length === 0) return;

    // Recursive function to collect paths for checked options and their parents
    const collectCheckedPaths = (options: any[], parentPath: string[] = [], paths: Set<string> = new Set()): Set<string> => {
      options.forEach((option: any) => {
        const currentPath = [
          ...parentPath,
          option.value?.toLowerCase() || option.label?.toLowerCase(),
        ];
        const optionValue = option.value?.toLowerCase() || option.label?.toLowerCase();
        
        // If this option is checked, add all parent paths
        if (checkedValues.includes(optionValue)) {
          for (let i = 1; i < currentPath.length; i++) {
            paths.add(currentPath.slice(0, i).join(">"));
          }
        }
        
        // Recursively check children
        if (option.childComponents && option.childComponents.length > 0) {
          collectCheckedPaths(option.childComponents, currentPath, paths);
        }
      });
      return paths;
    };

    const checkedPaths = collectCheckedPaths(optionsToRender);
    if (checkedPaths.size > 0) {
      setOpenPaths(prev => {
        const newSet = new Set(prev);
        checkedPaths.forEach(path => newSet.add(path));
        return newSet;
      });
    }
  }, [checkedComponents, selectedParentFilter, optionsToRender]);

  const isOwnerFilter = selectedParentFilter === "owner" || selectedParentFilter === "evidence-owner";
  const isSourceFilter = selectedFilter?.dbFieldPath?.endsWith('.origin');
  const isProductAreaFilter =
    selectedParentFilter === "product-area" ||
    selectedParentFilter === "evidence-product-area";
  const isReleaseFilter = selectedParentFilter === RELEASE_FILTER_KEY;

  // Recursive function to render options, including childComponents
  const renderOptions = useCallback((options: any, parentPath: string[] = []) => {
    if (!options || !Array.isArray(options)) return null;

    return options.filter(opt => opt != null).map((option: any, index: number) => {
      const optionValue = String(option.value || option.label || '').toLowerCase();
      const currentPath = [...parentPath, optionValue];
      const pathKey = currentPath.join(">");
      const hasChildren = !!(option.childComponents && option.childComponents.length > 0);

      // Simplified open check - avoid expensive recursive checks during render
      const isOpen = openPaths.has(pathKey) || autoOpenPaths.has(pathKey);

      const isCurrentUser = !!(isOwnerFilter &&
        currentUserEmail &&
        option.email &&
        option.email.toLowerCase() === currentUserEmail &&
        optionValue !== 'unassigned');

      const isReleaseGroupRow =
        isReleaseFilter && option.type === RELEASE_GROUP_OPTION_TYPE;

      // Every release group is tickable: a group is a tag, so "every release in this group"
      // is a legitimate set both to filter by and to assign. The row this replaces was
      // untickable inside a single-select group, where that set was a guaranteed 400.
      const isUntickableReleaseGroup = false;

      return (
        <div
          key={option.value || option.label || index}
          style={{
            paddingLeft: parentPath.length * 20,
            ...(isOwnerFilter && isCurrentUser ? { borderBottom: "1px solid #e9ecef", paddingBottom: 6 } : {}),
          }}
        >
          <OptionRow
            option={option}
            pathKey={pathKey}
            hasChildren={hasChildren}
            isOpen={isOpen}
            isOwnerFilter={isOwnerFilter}
            isSourceFilter={!!isSourceFilter}
            isProductAreaFilter={isProductAreaFilter}
            isReleaseFilter={isReleaseFilter}
            isReleaseGroupRow={isReleaseGroupRow}
            isUntickableReleaseGroup={isUntickableReleaseGroup}
            isCurrentUser={isCurrentUser}
            query={localSearchQuery}
            onToggle={toggleOpen}
          />
          {hasChildren && isOpen && renderOptions(option.childComponents, currentPath)}
        </div>
      );
    });
  }, [openPaths, autoOpenPaths, isOwnerFilter, isSourceFilter, isProductAreaFilter, isReleaseFilter, currentUserEmail, localSearchQuery, toggleOpen]);

  const handleCheckedComponentsChange = (selectedArray: string[]) => {

    if (selectedParentFilter) {
      const prevSelectedArray = checkedComponents[selectedParentFilter] || [];

      // Find out which checkbox was toggled
      const addedValues = selectedArray.filter(
        (value) => !prevSelectedArray.includes(value)
      );
      const removedValues = prevSelectedArray.filter(
        (value: any) => !selectedArray.includes(value)
      );

      const toggledValue =
          addedValues.length > 0 ? addedValues[0] : removedValues[0];
      const isAdding = addedValues.length > 0;

      selectedArray = applyParentChildToggle(
        processedOptions,
        selectedArray,
        toggledValue,
        isAdding
      );

      // Update the state
      setCheckedComponents((prev: any) => ({
        ...prev,
        [selectedParentFilter]: selectedArray,
      }));

      // Apply to discovery store immediately for idea (owner/product-area/release), or for evidence when
      // Explore passes setValue (e.g. All sources) — otherwise store only updates on Apply/close.
      // `release` rides this branch because the assignment picker saves on
      // every toggle, the way the tags cell does — there is no Apply step.
      const pushToExternalStore =
        setValue &&
        selectedParentFilter &&
        ((MenuType === 'idea' && _.includes(['owner', 'product-area', RELEASE_FILTER_KEY], selectedParentFilter)) ||
          MenuType === 'evidence');
      if (pushToExternalStore) {
        setValue(selectedArray.length > 0 ? selectedArray : []);
      }
    }
  };
  // console.log({selectedOptions});

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;
      setLocalSearchQuery(newValue);
      selectedParentFilter &&
        setChildQuery((prev: any) => ({
          ...prev,
          [selectedParentFilter]: newValue,
        }));
    },
    [selectedParentFilter]
  );

  // Automatically open the root domain if there is only one domain at the root level and no query
  useEffect(() => {
    if (
      (isProductAreaFilter || isReleaseFilter) &&
      Array.isArray(optionsToRender) &&
      optionsToRender.length === 1 &&
      optionsToRender[0].childComponents &&
      optionsToRender[0].childComponents.length > 0 &&
      !debouncedQuery
    ) {
      const domain = optionsToRender[0];
      const pathKey = String(domain.value || domain.label || '').toLowerCase();
      if (!openPaths.has(pathKey)) {
        setOpenPaths(prev => new Set(prev).add(pathKey));
      }
    }
  }, [optionsToRender, isProductAreaFilter, isReleaseFilter, debouncedQuery]);

  return (
    selectedParentFilter && (
      <>
        {selectedHasOperations && (
          <OperationSelect
            operations={selectedOperators}
            onChange={(value) => {
              setOperator((prev: any) => ({
                ...prev,
                [selectedParentFilter]: value,
              }));
            }}
          />
        )}
        <TextInput
          mx={5}
          radius="md"
          value={childQuery && childQuery[selectedParentFilter]}
          placeholder={`Search ${_selectedParentFilter[
            _selectedParentFilter.length - 1
          ].toLocaleLowerCase()}`}
          onChange={handleInputChange}
          icon={<IconListSearch size={14.5} />}
          sx={{
            input: {
              backgroundColor: "#F9FAFB",
              ":focus": {
                borderColor: "#b1b1b1;",
              },
            },
          }}
        />
        <ScrollArea
          style={{
            height:
                      selectedParentFilter === "evidence_priority" ? 150 : 280,
          }}
        >
          <Checkbox.Group
            mt={8}
            value={checkedComponents[selectedParentFilter]}
            onChange={handleCheckedComponentsChange}
          >
            {renderOptions(optionsToRender)}
          </Checkbox.Group>
        </ScrollArea>
        <FilterFooter
          clearLabel={clearLabel}
          onClickClear={() => {

            if (typeof setOpened === 'function') {
              try { setOpened(false); } catch {}
            }

            resetFilters(MenuType, selectedParentFilter)
          }
           
          }
          ApplyButton={
            <AddFilterBtn
              BtnText="Apply"
              hidden={hideAddButton}
              selectedParentFilter={selectedParentFilter}
              childFiltersValue={childFiltersValue}
              disabled={
                (!checkedComponents[selectedParentFilter] ||
                              checkedComponents[selectedParentFilter]?.length === 0) ||
                          ((selectedParentFilter !== null &&
                                  childFiltersValue[selectedParentFilter]?.length === 0) ||
                              (selectedHasOperations && !operator))
              }
              onClick={() => {
                // If a parent menu controls open state, close it first for snappy UX
                if (typeof setOpened === 'function') {
                  try { setOpened(false); } catch {}
                }
                const parentFilterObj = findFiltersByPath(
                  ideasFiltersConfig,
                  `${MenuType}.${selectedParentFilter}`
                );

                // Ticking a release *group* cascades its synthetic parent value
                // (`release-group:<id>`) into the selection alongside the real
                // ids. It must not be submitted: the filter is registered
                // `selectedIsObjId: true`, so the backend casts each value to
                // an ObjectId and that string is not one. Drop it here, at the
                // boundary, rather than in the checkbox state — the group
                // should stay visibly ticked.
                const selectedIds = (
                  checkedComponents[selectedParentFilter] ?? []
                ).filter(
                  (value: any) => !isReleaseGroupOptionValue(String(value))
                );
                const selectedFilterMap = buildSelectedFilterMapFromOptions(
                  selectedIds.map(String),
                  selectedOptions
                );

                const filterObj = {
                  ...parentFilterObj,
                  type: parentFilterObj?.type,
                  parent: selectedParentFilter,
                  operator:
                    operator && operator[selectedParentFilter],
                  selected: selectedIds,
                  ...(selectedFilterMap && { selectedFilterMap }),
                  query: null
                }

                if (
                  setValue &&
                  selectedParentFilter &&
                  ((MenuType === 'idea' && _.includes(['owner', 'product-area', RELEASE_FILTER_KEY], selectedParentFilter)) ||
                    MenuType === 'evidence')
                ) {
                  const selected = filterObj?.selected || null;
                  setValue(selected);
                }
                setActiveFilters((prevFilters: any) => [
                  filterObj,
                  ...prevFilters.filter(
                    (prevF: any) =>
                      prevF.parent !== selectedParentFilter
                  ),
                ]);
                setOpenedMenuType(null);
                setSelectedParentFilter(null);
              }}
            />
          }
        />
      </>
    )
  );
};

export default CheckboxFilter;